import React from "react";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/contexts/LanguageContext";

const SEO = ({
  title,
  description,
  keywords,
  image = "/og-image.png",
  url,
  type = "website",
  structuredData,
  noindex = false,
}) => {
  const { lang } = useLang();
  
  const siteUrl = "https://miskkargo.com";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullImage = image.startsWith("http") ? image : `${siteUrl}${image}`;

  const defaultKeywords = {
    ar: "شحن من تركيا, شحن من الصين, شحن الى سوريا, مسك كارجو, شحن دولي, استيراد من تركيا, استيراد من الصين, شحن بري, شحن بحري, شحن جوي, تخليص جمركي, شحن الى دمشق, شحن الى حلب",
    en: "shipping from Turkey, shipping from China, shipping to Syria, Misk Cargo, international shipping, import from Turkey, import from China, land freight, sea freight, air freight, customs clearance, shipping to Damascus, shipping to Aleppo",
    tr: "Türkiye'den kargo, Çin'den kargo, Suriye'ye kargo, Misk Cargo, uluslararası kargo, Türkiye'den ithalat, Çin'den ithalat, karayolu taşımacılığı, denizyolu taşımacılığı, havayolu taşımacılığı, gümrük işlemleri, Şam'a kargo, Halep'e kargo",
    "zh-CN": "土耳其货运, 中国货运, 叙利亚货运, Misk Cargo, 国际货运, 土耳其进口, 中国进口, 陆运, 海运, 空运, 清关服务, 大马士革货运, 阿勒颇货运, 中叙物流, 土叙物流"
  };

  const finalKeywords = keywords || defaultKeywords[lang] || defaultKeywords.en;

  // Generate hreflang URLs for all supported languages
  const hreflangs = [
    { lang: "ar", url: `${fullUrl}?lang=ar` },
    { lang: "en", url: `${fullUrl}?lang=en` },
    { lang: "tr", url: `${fullUrl}?lang=tr` },
    { lang: "zh-CN", url: `${fullUrl}?lang=zh-CN` },
    { lang: "x-default", url: fullUrl }
  ];

  // Locale mapping for Open Graph
  const ogLocaleMap = {
    ar: "ar_SY",
    en: "en_US",
    tr: "tr_TR",
    "zh-CN": "zh_CN"
  };

  const ogLocale = ogLocaleMap[lang] || "en_US";
  const alternateLocales = Object.values(ogLocaleMap).filter(l => l !== ogLocale);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={finalKeywords} />
      <link rel="canonical" href={fullUrl} />

      {/* Hreflang Tags for International SEO */}
      {hreflangs.map(({ lang: hrefLang, url: hrefUrl }) => (
        <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={hrefUrl} />
      ))}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Misk Cargo" />
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map(locale => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Additional SEO Tags */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* China-specific search engines */}
      <meta name="baidu-site-verification" content="codeva-miskkargo" />
      <meta name="360-site-verification" content="miskkargo360" />
      <meta name="sogou_site_verification" content="miskkargosogou" />
      
      <meta name="author" content="Misk Cargo" />
      <meta name="geo.region" content="TR;SY;CN" />
      <meta name="geo.placename" content="Istanbul;Damascus;Aleppo;Guangzhou;Yiwu" />
      
      {/* Mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
